import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import {
  TextField,
  Button,
  Modal,
  Grid,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  Box,
} from "@mui/material";
import { INewUserAdmin } from "../../../api/admin";
import { IAdminUser } from "../../../interface/IAdminUser";
import { ClinicSearchForm } from "./search-clinic";

import { listClinicQuery } from "../../../api/clinic";

interface Props {
  userId: string;
  onSubmit: (data: INewUserAdmin) => void;
  onSumitUpdate: (userId: string, data: INewUserAdmin) => void;
  showForm: (bool: boolean) => void;
  users: IAdminUser[] | {};
  setUserId: (userId: string) => void;
}

export const CreateUserForm: FC<Props> = ({
  onSubmit,
  showForm,
  userId,
  onSumitUpdate,
  users,
  setUserId,
}) => {
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    clinicName: "",
  });
  const [open, setOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState("");
  const [clinicsData, setClinicData] = useState([]);
  const [queryClinic, setQueryClinic] = useState({
    page: "1",
    perPage: "20",
    search: "",
  });

  const firstNameRef = useRef<HTMLInputElement>(null);

  const fechDataClinicList = useCallback(async () => {
    try {
      const responseClinicsQuery = await listClinicQuery(
        queryClinic.page,
        queryClinic.perPage,
        queryClinic.search
      );
      setClinicData(responseClinicsQuery?.clinics);
    } catch (error) {
      console.error("Error fetching clinics: ", error);
    }
  }, [queryClinic.page, queryClinic.perPage, queryClinic.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handleChangeSelect = (e: SelectChangeEvent) => {
    setUserData({ ...userData, role: e.target.value });
  };

  const getUserEdit = useCallback(
    (userId: string) => {
      if (Array.isArray(users) && users.length > 0) {
        const user = users.find((user) => user._id === userId);
        if (user) {
          setUserData({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            role: user.role,
            clinicName: "",
          });
        }
      }
    },
    [users]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (userId) {
      onSumitUpdate(userId, userData);
      setUserData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        clinicName: "",
      });
      showForm(false);
      setUserId("");
    } else {
      setUserData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        clinicName: "",
      });
      onSubmit(userData);
    }
  };

  const handleChangeClinic = () => {
    setSelectedClinic("");
    setOpen(true);
  };

  useEffect(() => {
    if (userId) {
      getUserEdit(userId);
      setOpen(false);
    } else {
      setUserData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
        clinicName: "",
      });
    }
  }, [getUserEdit, userId]);

  useEffect(() => {
    if (userData.firstName && firstNameRef.current) {
      firstNameRef.current.focus();
    }

    if (userData.role === "clinic-dashboard") {
      setOpen(true);
    }

    if (selectedClinic) {
      setUserData((prevData) => ({
        ...prevData,
        clinicName: selectedClinic,
      }));
    }
    fechDataClinicList();
  }, [fechDataClinicList, userData.role, selectedClinic, userData.firstName]);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={10} sm={8}>
        <Typography variant="h6" gutterBottom>
          {userId ? "Benutzer Aktualisieren" : " Neuen Benutzer Erstellen"}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container justifyContent="center">
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="firstName"
              name="firstName"
              label="Vorname"
              type="text"
              value={userData.firstName}
              onChange={handleChange}
              inputRef={firstNameRef}
            />
            <TextField
              variant="outlined"
              margin="normal"
              type="text"
              fullWidth
              id="lastName"
              name="lastName"
              label="Nachname"
              value={userData.lastName}
              onChange={handleChange}
            />
            <Grid item xs={12} sm={6} pr={1}>
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                type="tel"
                id="phone"
                name="phone"
                label="Telefon"
                value={userData.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              pl={1}
              mt={1}
              alignContent="center"
              justifyContent="center"
            >
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">Rolle</InputLabel>
                <Select
                  labelId="rolle"
                  id="rolleId"
                  value={userData.role}
                  label="Rolle"
                  onChange={handleChangeSelect}
                >
                  <MenuItem value={"admin"}>Administrator</MenuItem>
                  <MenuItem value={"employee"}>Mitarbeiter</MenuItem>
                  <MenuItem value={"clinic-dashboard"}>
                    Klinikschachtel
                  </MenuItem>
                  <MenuItem value={"clinic-weight"}>Klinikgewicht</MenuItem>
                  <MenuItem value={"clinic-preview"}>Klinikvorschau</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {userData.role === "clinic-dashboard" && !userId && (
              <Modal
                open={open}
                onClose={() => setOpen(false)}
                sx={{
                  padding: "10px",
                  margin: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "60%",
                    height: "100%",
                    backgroundColor: "white",
                    marginTop: "30px",
                    borderRadius: "5px",
                    margin: "auto",
                    marginBottom: "15px",
                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <ClinicSearchForm
                    clinics={clinicsData}
                    setQueryClinic={setQueryClinic}
                    queryClinic={queryClinic}
                    setSelectedClinic={setSelectedClinic}
                    selectedClinic={selectedClinic}
                    setOpen={setOpen}
                  />
                </Box>
              </Modal>
            )}
            {selectedClinic && (
              <Grid
                container
                display="flex"
                justifyContent="space-between"
                alignContent="center"
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ marginTop: "16px" }}
                  >
                    Ausgewählte Klinik:
                    <span style={{ fontWeight: 600 }}> {selectedClinic}</span>
                  </Typography>
                </Box>
                <Box alignContent="center">
                  <Button variant="contained" onClick={handleChangeClinic}>
                    Klinik ändern
                  </Button>
                </Box>
              </Grid>
            )}
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="email"
              name="email"
              label="E-mail"
              type="email"
              value={userData.email}
              onChange={handleChange}
            />
            {userId ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Aktualisieren
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Benutzer Erstellen
              </Button>
            )}
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};
